'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import { X } from 'lucide-react'
import { getResizedPictureUrl, getVideoUrl } from '@/lib/utils'
import type { WelcomeModalSettings } from '@/types/meta'

const SESSION_KEY = 'welcome_modal_shown'

type Props = {
  settings?: WelcomeModalSettings | null
}

const WelcomeModal = ({ settings }: Props) => {
  'use no memo'

  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!settings?.enabled) {
      return
    }
    if (sessionStorage.getItem(SESSION_KEY)) {
      return
    }
    setOpen(true)
    // settings is a stable SSR prop — safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = () => {
    setOpen(false)
    sessionStorage.setItem(SESSION_KEY, '1')
  }

  if (!open || !settings?.enabled) {
    return null
  }

  return (
    <>
      <div className="fixed inset-0 z-9998 bg-black/60 backdrop-blur-sm" onClick={handleClose} aria-hidden="true" />
      <div className="fixed top-1/2 left-1/2 z-9999 w-full max-w-5xl -translate-x-1/2 -translate-y-1/2 px-4">
        <div className="bg-background relative max-h-192 overflow-hidden rounded-2xl shadow-2xl">
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-3 right-3 z-10 rounded-full bg-black/50 p-1.5 text-white hover:opacity-80 focus:outline-none"
            aria-label="Close">
            <X className="h-4 w-4" />
          </button>

          {settings.type?.toLowerCase() === 'image' && settings.image ? (
            settings.link ? (
              <a href={settings.link} target="_blank" rel="noopener noreferrer">
                <div className="relative aspect-video w-full">
                  <Image
                    src={getResizedPictureUrl(settings.image.path, 800, 450)}
                    alt={settings.image?.name || 'Welcome Banner'}
                    fill
                    className="object-cover"
                    priority
                  />
                </div>
              </a>
            ) : (
              <div className="relative aspect-video w-full">
                <Image
                  src={getResizedPictureUrl(settings.image.path, 800, 450)}
                  alt={settings.image?.name || 'Welcome Banner'}
                  fill
                  className="object-cover"
                  priority
                />
              </div>
            )
          ) : settings.type?.toLowerCase() === 'video' && settings.video ? (
            <div className="relative aspect-video w-full">
              <video
                // React doesn't reliably set the `muted` DOM property from JSX, which makes browsers
                // block autoplay. Force it via ref and start playback immediately.
                ref={el => {
                  if (el) {
                    el.muted = true
                    el.play().catch(() => {})
                  }
                }}
                src={getVideoUrl(settings.video.path)}
                className="h-full w-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                controls
              />
            </div>
          ) : settings.type?.toLowerCase() === 'youtube' && settings.videoUrl ? (
            <div className="relative aspect-video w-full">
              <iframe
                src={(() => {
                  try {
                    const url = new URL(settings.videoUrl!)
                    url.searchParams.set('autoplay', '1')
                    url.searchParams.set('mute', '1')
                    return url.toString()
                  } catch {
                    return settings.videoUrl!
                  }
                })()}
                title="Welcome"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default WelcomeModal
